import { httpsCallable } from "firebase/functions";
import { Timestamp } from "firebase/firestore";
import { functions } from "../firebase";

// Data source for the India Bookings, Lead Pool and notification bell. The
// pages are the Australian ones; only the data comes from India admin
// callables (admin check happens on the server).

const POLL_MS = 30000;
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

const call = async (name, payload = {}) => {
  try {
    const result = await httpsCallable(functions, name)(payload);
    return result.data || {};
  } catch (error) {
    // A bare "internal" means the function could not be reached at all.
    if (error?.code === "functions/internal" && error?.message === "internal") {
      throw new Error("India admin service is not reachable. Please try again later.");
    }
    throw error;
  }
};

// Callables return timestamps as ISO strings; the pages expect Firestore
// Timestamps (toDate()), as they get from the Australian database.
const withTimestamps = (record = {}) =>
  Object.fromEntries(
    Object.entries(record).map(([key, value]) => [
      key,
      typeof value === "string" && ISO_DATE_TIME.test(value)
        ? Timestamp.fromDate(new Date(value))
        : value,
    ])
  );

const toBooking = (booking) => {
  const record = withTimestamps(booking);
  // India bookings keep the day in selectedDate; the list shows scheduledDate.
  return { ...record, scheduledDate: record.scheduledDate || record.selectedDate || null };
};

const OVERVIEW_CACHE_MS = 60000;
let overviewCache = null;
let overviewCacheAt = 0;
const loadOverview = (fresh) => {
  if (fresh || !overviewCache || Date.now() - overviewCacheAt > OVERVIEW_CACHE_MS) {
    overviewCacheAt = Date.now();
    overviewCache = call("adminGetIndiaOverview")
      .then(({ users = [], services = [], bookings = [] }) => ({
        users: users.map(withTimestamps),
        services: services.map(withTimestamps),
        bookings: bookings.map(toBooking),
      }))
      .catch((error) => {
        overviewCache = null;
        throw error;
      });
  }
  return overviewCache;
};

// Booking lists poll; an assignment refreshes every open list at once.
const bookingRefreshers = new Set();
const supportRefreshers = new Set();
const SUPPORT_POLL_MS = 15000;

/** Polls `load` like onSnapshot: returns an unsubscribe; `refreshers` lets a write refresh at once. */
const poll = (load, onData, onError, refreshers) => {
  let active = true;
  const refresh = async () => {
    try {
      const items = await load();
      if (active) onData(items);
    } catch (error) {
      if (active && onError) onError(error);
    }
  };
  refreshers.add(refresh);
  refresh();
  const timer = setInterval(refresh, SUPPORT_POLL_MS);
  return () => {
    active = false;
    clearInterval(timer);
    refreshers.delete(refresh);
  };
};

export const indiaOperations = {
  country: "IN",
  // Service GST is left out until the client's tax details are confirmed.
  gstRate: null,
  formatCurrency: (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return "N/A";
    return `₹${parsed.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  },

  /** Same contract as onSnapshot: calls onData with the list, returns an unsubscribe. */
  subscribeBookings: (onData, onError) => {
    let active = true;
    const refresh = async () => {
      try {
        const { bookings = [] } = await call("adminListBookingsIn");
        if (active) onData(bookings.map(toBooking));
      } catch (error) {
        if (active) onError(error);
      }
    };
    bookingRefreshers.add(refresh);
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    return () => {
      active = false;
      clearInterval(timer);
      bookingRefreshers.delete(refresh);
    };
  },

  /** Approved, active providers with location and the India services they offer. */
  loadProviders: async () => (await call("adminListAssignableProvidersIn")).providers || [],

  assignBooking: async (bookingId, providerId) => {
    await call("adminAssignBookingIn", { bookingId, providerId });
    bookingRefreshers.forEach((refresh) => refresh());
  },

  loadLeads: async () => ((await call("adminListLeadsIn")).leads || []).map(withTimestamps),

  assignLead: (leadId, providerId) => call("adminAssignLeadIn", { leadId, providerId }),

  listNotifications: async () => (await call("adminListNotificationsIn")).notifications || [],

  /** @param {"read"|"delete"} action */
  updateNotifications: (ids, action) => call("adminUpdateNotificationsIn", { ids, action }),

  // Customers page.
  currencySymbol: "₹",
  listCustomers: async () => (await call("adminListIndiaCustomers")).customers || [],
  getCustomerProfile: async (customerId) => {
    const { customer = {}, bookings = [] } = await call("adminGetIndiaCustomer", { customerId });
    return { customer, bookings: bookings.map(toBooking) };
  },
  setCustomerActive: (customerId, active) =>
    call("adminSetIndiaCustomerActive", { customerId, active }),

  // Dashboard overview: one call returns users, services and bookings; the
  // details dialog reuses the last answer for a minute.
  loadOverview: () => loadOverview(true),
  overviewRows: async (detailsType) => {
    const { users, services, bookings } = await loadOverview(false);
    const nameOf = new Map(users.map((u) => [u.id, u.name || u.displayName || u.email || ""]));
    const isCustomer = (u) => u.roles?.customer === true || u.role === "customer";
    const isProvider = (u) => u.roles?.client === true || u.roles?.provider === true || u.role === "provider";
    if (detailsType === "customers") return users.filter(isCustomer);
    if (detailsType === "providers") {
      return users.filter(isProvider).map((u) => ({
        ...u,
        _servicePreview:
          services.find((s) => s.ownerId === u.id || (s.providers || []).includes(u.id))?.title || "",
      }));
    }
    if (detailsType === "services") {
      return services.map((s) => ({
        ...s,
        _providerName: s.providerName || nameOf.get(s.ownerId || (s.providers || [])[0]) || "",
      }));
    }
    if (detailsType === "bookings") return bookings;
    return users;
  },

  // Support desk. Timestamps stay ISO strings; the page formats both kinds.
  subscribeSupportCases: (onData, onError) =>
    poll(async () => (await call("adminListIndiaSupportCases")).cases || [], onData, onError, supportRefreshers),
  subscribeSupportMessages: (caseId, onData, onError) =>
    poll(
      async () => (await call("adminGetIndiaSupportMessages", { caseId })).messages || [],
      onData,
      onError,
      supportRefreshers
    ),
  replySupportCase: async (caseId, message) => {
    await call("adminReplyIndiaSupportCase", { caseId, message });
    supportRefreshers.forEach((refresh) => refresh());
  },
  setSupportCaseStatus: async (caseId, status) => {
    await call("adminSetIndiaSupportCaseStatus", { caseId, status });
    supportRefreshers.forEach((refresh) => refresh());
  },
};
